import React, { useEffect, useState } from "react";
import { listWorkflows, runWorkflow } from "@/lib/api";
import { toast } from "sonner";
import { Play } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Workflows() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState({});
    const [target, setTarget] = useState("operator@proofcommand.dev");
    const navigate = useNavigate();

    useEffect(() => {
        listWorkflows().then(setItems);
    }, []);

    const run = async (wf) => {
        setBusy((b) => ({ ...b, [wf.id]: true }));
        try {
            const res = await runWorkflow(wf.id, { target });
            const last = res.actions?.[res.actions.length - 1];
            toast.success(
                `Ran ${wf.name} · ${res.actions.length} step(s) · last: ${last?.status}`
            );
            if (last?.status === "needs_review") {
                navigate("/review");
            } else {
                navigate("/log");
            }
        } catch (e) {
            toast.error("Workflow run failed");
        } finally {
            setBusy((b) => ({ ...b, [wf.id]: false }));
        }
    };

    return (
        <div data-testid="workflows-page">
            <div className="mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c] mb-2">
                    // templates
                </div>
                <h1 className="text-4xl font-bold tracking-tight">Workflows</h1>
                <p className="font-mono text-[11px] text-[#9ca3af] mt-1">
                    Lean templates. Every step becomes a logged, retryable action.
                </p>
            </div>

            <div className="flex items-center gap-3 mb-6">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af]">
                    target
                </label>
                <input
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    data-testid="workflow-target-input"
                    className="w-80 bg-[#111315] border border-[#262a2e] font-mono text-[12px] px-3 py-2 rounded-[2px] focus:border-[#7cc93c] outline-none"
                />
            </div>

            <div className="border border-[#262a2e] bg-[#111315]">
                {items.map((wf) => (
                    <div
                        key={wf.id}
                        className="pc-row px-5 py-4 border-b last:border-b-0 border-[#262a2e] flex items-center gap-6"
                        data-testid={`workflow-row-${wf.id}`}
                    >
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                                <span className="font-sans font-semibold text-[15px]">
                                    {wf.name}
                                </span>
                                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9ca3af] border border-[#262a2e] px-1.5 py-0.5 rounded-[2px]">
                                    {wf.id}
                                </span>
                            </div>
                            <div className="font-mono text-[11px] text-[#9ca3af] mt-1">
                                {wf.description}
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                {wf.steps.map((s, i) => (
                                    <React.Fragment key={i}>
                                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9ca3af] border border-[#262a2e] px-1.5 py-0.5 rounded-[2px]">
                                            {s.type}
                                        </span>
                                        {i < wf.steps.length - 1 && (
                                            <span className="text-[#3a3f44]">→</span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                        <button
                            onClick={() => run(wf)}
                            disabled={busy[wf.id]}
                            data-testid={`workflow-run-${wf.id}`}
                            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border border-[#7cc93c] text-[#7cc93c] hover:bg-[#7cc93c] hover:text-black transition-colors px-4 py-2 rounded-[2px] disabled:opacity-50"
                        >
                            <Play className="w-3 h-3" />
                            {busy[wf.id] ? "Running…" : "Run workflow"}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
